import {assert} from 'Asserts';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {Optional} from 'util/Types';

export enum OptionContentType {
  ANCHOR = 'ANCHOR', // Indicate that the OptionContent should be wrapped in an <a>.
  INPUT = 'INPUT', // Indicate that the OptionContent will contain an <input>; ensures correct focus/padding
  PLAIN = 'PLAIN',
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type OptionValue = any;

export interface OptionData {
  className?: string;
  disabled?: boolean;

  // A message to add to the value when the option is disabled
  disabledMessage?: string;

  // Any additional context that one might want to attach to the option
  extra?: {unstarrable?: boolean; headerId?: string};
  isHeader?: boolean;
  icon?: BenchlingIconName;
  iconClassName?: string;
  name: string | JSX.Element;
  pureTextName?: string;
  value?: OptionValue;
  submenu?: Array<Array<Optional<OptionData>>> | null;
  skipIconOverride?: boolean;
  hidden?: boolean;
  dataTestAttrs?: DataTestAttrs;
  /**
   * Rendered as a `data-amp-safe` DOM attribute. The Amplitude privacy plugin only
   * transmits `data-test-key` from nodes that opt in with `data-amp-safe={true}`;
   * set this to false when `dataTestAttrs.key` can hold customer-specified values.
   */
  dataAmpSafe?: boolean;
  metadata?: string | null;
  tooltipTitle?: string | null;
}

export const LOADING_OPTION_DATA: OptionData = {
  name: 'Loading...',
  value: null,
  pureTextName: '',
  disabled: true,
};

export const ERROR_OPTION_DATA: OptionData = {
  name: 'Error',
  value: null,
  pureTextName: '',
  disabled: true,
};

export function getOptionDataPureTextName(optionData: OptionData): string {
  if (optionData.pureTextName != null) {
    return optionData.pureTextName;
  }
  assert(
    typeof optionData.name === 'string',
    'An OptionData must either have a `name` of type string or define `pureTextName`',
    {optionData}
  );
  return optionData.name;
}

export enum MenuHeight {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  XLARGE = 'xl',
}

export enum MenuWidth {
  MINI = 'mini',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  XLARGE = 'xl',
  XXLARGE = 'xxl',
  FULL = 'full',
  FIT_CONTENT = 'fitContent',
}

/**
 * Where the default **selection checkmark** (`check` icon) is rendered relative to an option’s primary
 * leading `icon` / `avatar`, **when** `Menu` injects that check (`isMulti`, or single-select with
 * `useSelectedIconIfSingleSelect` on the selected row—see `MenuProps.selectedIconPlacement`).
 *
 * **Leading** (`leading`): single-select with `useSelectedIconIfSingleSelect` replaces the leading icon
 * with the check when selected; multi-select uses leading checks with hidden placeholders when unselected.
 *
 * **Trailing** (`trailing`): the primary `icon` / `avatar` stays on the left and the check is shown in a
 * fixed trailing slot (including hidden trailing checks on unselected rows in multi-select for alignment).
 */
export enum MenuSelectedIconPlacement {
  /**
   * Checkmark uses the **leading** icon slot (same as leaving `selectedIconPlacement` unset):
   * - **Single-select** with `useSelectedIconIfSingleSelect`: the check **replaces** the option’s primary
   *   icon when selected.
   * - **Multi-select** without {@link MenuSelectedIconPlacement.TRAILING}: selected rows show the check as the
   *   leading icon; unselected rows keep a hidden leading check for alignment.
   */
  LEADING = 'leading',
  /**
   * Checkmark is rendered in a **fixed trailing** slot; the option’s primary `icon` / `avatar` stays in
   * the leading slot. On multi-select menus, unselected rows still reserve a hidden trailing check so the
   * column lines up.
   */
  TRAILING = 'trailing',
}
